import type { AuthUser } from '@stayon/auth-core';
import { can, canAccess, isPlatformAdmin, isPlatformWorkspace } from '@/shared/auth';
import {
  accountEntries,
  platformEntries,
  settingsEntry,
  shellAreas,
  type NavArea,
  type NavEntry,
  type NavSearch,
} from './adminNavigation';

/** Dieselbe Antwort wie die Route-Guards: Recht, sonst Rolle, dazu die Plattform-Grenzen. */
export function isEntryVisible(user: AuthUser | null, entry: NavEntry): boolean {
  if (!user) return false;
  if (entry.homeSessionOnly && user.supportSessionId) return false;
  if (entry.platformOnly) return isPlatformAdmin(user);
  if (entry.platformWorkspaceOnly && !isPlatformWorkspace(user)) return false;
  if (entry.requireRoles && !canAccess(user, entry.requireRoles)) return false;
  const allowed = entry.privilege ? can(user, entry.privilege) : canAccess(user, entry.roles);
  return allowed || Boolean(entry.alsoPlatformAdmin && isPlatformAdmin(user));
}

/** Bereiche mit ihren sichtbaren Einträgen; ein Bereich ohne sichtbaren Eintrag fehlt. */
export function visibleAreas(user: AuthUser | null): NavArea[] {
  return shellAreas
    .map((area) => ({ ...area, entries: area.entries.filter((e) => isEntryVisible(user, e)) }))
    .filter((area) => area.entries.length > 0);
}

export const visibleAccountEntries = (user: AuthUser | null) =>
  accountEntries.filter((entry) => isEntryVisible(user, entry));

export const visiblePlatformEntries = (user: AuthUser | null) =>
  platformEntries.filter((entry) => isEntryVisible(user, entry));

export const settingsVisible = (user: AuthUser | null) => isEntryVisible(user, settingsEntry);

function searchMatches(entry: NavEntry, search: NavSearch): boolean {
  return Object.entries(entry.search ?? {}).every(
    ([key, value]) => String(search[key] ?? '') === value,
  );
}

/**
 * Aktiver Eintrag in einer Liste. Ein Eintrag mit eigenen Suchparametern (Reinigung › Woche) gewinnt
 * vor seinem Geschwister auf derselben Route, damit nie zwei Einträge zugleich leuchten.
 */
export function activeEntryIn(
  entries: NavEntry[],
  path: string,
  search: NavSearch,
): NavEntry | null {
  const specific = entries.find((e) => e.search && path === e.to && searchMatches(e, search));
  if (specific) return specific;
  return entries.find((e) => !e.search && (path === e.to || e.matches?.(path, search))) ?? null;
}

export type ActivePlace =
  | { kind: 'area'; area: NavArea; entry: NavEntry }
  | { kind: 'platform' | 'account' | 'settings'; entry: NavEntry }
  | null;

/** Wo bin ich? Plattform und Konto zuerst, weil ihre Seiten teils auch zu den Einstellungen zählen. */
export function resolveActivePlace(
  user: AuthUser | null,
  path: string,
  search: NavSearch,
): ActivePlace {
  const platform = activeEntryIn(visiblePlatformEntries(user), path, search);
  if (platform) return { kind: 'platform', entry: platform };
  const account = activeEntryIn(visibleAccountEntries(user), path, search);
  if (account) return { kind: 'account', entry: account };
  for (const area of visibleAreas(user)) {
    const entry = activeEntryIn(area.entries, path, search);
    if (entry) return { kind: 'area', area, entry };
  }
  if (settingsVisible(user) && activeEntryIn([settingsEntry], path, search)) {
    return { kind: 'settings', entry: settingsEntry };
  }
  return null;
}
